import { useMemo, useState } from 'react';

// Keys que nunca deben mostrarse como campo dinámico del formulario.
const EXCLUDED_KEYS = ['id_flow', 'flow_token'];

const extractMessage = (payload) => {
    if (!payload) return null;

    if (payload.interactive || payload.url_path_whatsapp) return payload;

    try {
        return payload
            .entry?.[0]
            ?.changes?.[0]
            ?.value
            ?.messages?.[0] ?? null;
    } catch (e) {
        console.error('No se pudo ubicar el mensaje dentro del payload:', e);
        return null;
    }
};

/**
 * useSupportResponse
 *
 * Extrae de forma DINÁMICA todos los campos informativos del response_json
 * (cualquier key que no sea de control ni un array de adjuntos),
 * y los anexos reales desde url_path_whatsapp.
 *
 * Retorna:
 *  - fields  {Array<{ key, label, value }>}  campos dinámicos a renderizar
 *  - anexos  {string[]}                       paths de url_path_whatsapp
 *  - open / onOpen / onClose
 */
export const useSupportResponse = (rawData) => {
    const [open, setOpen] = useState(false);

    const parsed = useMemo(() => {
        const empty = { fields: [], anexos: [] };
        if (!rawData) return empty;

        let payload = rawData;
        if (typeof rawData === 'string') {
            try {
                payload = JSON.parse(rawData);
            } catch (e) {
                console.error('No se pudo parsear el payload:', e);
                return empty;
            }
        }

        const message = extractMessage(payload);
        if (!message) return empty;

        // ── Campos dinámicos desde response_json ──
        let fields = [];
        const responseJsonRaw = message.interactive?.nfm_reply?.response_json;

        if (responseJsonRaw) {
            try {
                const responseJson =
                    typeof responseJsonRaw === 'string'
                        ? JSON.parse(responseJsonRaw)
                        : responseJsonRaw;

                fields = Object.entries(responseJson)
                    .filter(([key, value]) => {
                        if (EXCLUDED_KEYS.includes(key)) return false;
                        if (Array.isArray(value)) return false; // ej: "Anexo"
                        return true;
                    })
                    .map(([key, value]) => ({
                        key,
                        label: key.replace(/_/g, ' '), // ej: screen_0_Asunto_0 -> screen 0 Asunto 0
                        value,
                    }));
            } catch (e) {
                console.error('No se pudo parsear response_json:', e);
            }
        }

        // ── Anexos reales, validando que exista y tenga data ──
        const anexos =
            Array.isArray(message.url_path_whatsapp) && message.url_path_whatsapp.length > 0
                ? message.url_path_whatsapp
                : [];

        return { fields, anexos };
    }, [rawData]);

    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);

    return { open, onOpen, onClose, ...parsed };
};